import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Input, Select, Switch, InputNumber, Toast, Spin, Pagination } from '@douyinfe/semi-ui';
import { API } from '../helpers';
import { usePagedRows } from '../helpers/usePagedRows';

const TYPES = [
  { value: 'openai',    label: 'OpenAI 兼容' },
  { value: 'anthropic', label: 'Anthropic 兼容' },
  { value: 'custom',    label: '自定义' },
];

const emptyChannel = { name: '', type: 'openai', base_url: '', api_key: '', model_pricing: '', context_limits: '{}', enabled: 1, priority: 0, max_concurrency: 5, remark: '' };
const CHANNEL_OPTIONS = TYPES.map((t) => ({ value: t.value, label: t.label }));

// 思考强度档位:空值 = 不注入(客户端传什么用什么,上游省略时按自身默认档)
const EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'];
const EFFORT_OPTIONS = [{ value: '', label: '不指定' }, ...EFFORT_LEVELS.map((e) => ({ value: e, label: e }))];

// 上下文窗口预设:token 数。空 = 不限制(不生成 ANTHROPIC_CONTEXT_LENGTH,也不对外声明)
// 这是「模型元数据」—— 告诉客户端这个模型最多能塞多少上下文,让它自己决定何时压缩,
// 不由网关拦截请求。
// 预设只留 MiniMax 官网实际在用的两档:
//   524288  (512K) —— MiniMax 官方 max_completion_tokens 上限 / 计价分档线(≤512k 与 >512k 两档定价)
//   1000000 (1M)   —— MiniMax-M3 / M3.1-Flash 的官方上下文窗口
// 其余值(接第三方模型时可能需要)走「自定义…」。
const CTX_PRESETS = [
  { value: 0,         label: '不限制' },
  { value: 524288,    label: '512K' },
  { value: 1000000,   label: '1M' },
];
const CTX_CUSTOM = -1; // 哨兵:选中后展开数字输入框

// 新建模型行的默认门槛:512K。
// 选 512K 而不是模型的真实上限 1M,是刻意的 —— MiniMax 的输入 token 按 512k 分档定价
// (≤512k 与 >512k 两档),把门槛设在 512K,客户把会话养过 512k 时按贵档价向他收费,
// 而不是让网关替他承担上游翻倍的成本。
// 每个模型仍可单独改成 1M(等于承诺按正常价服务到 1M,自己承担上游差额)或别的值。
const CTX_DEFAULT = 524288;

// 下拉选项:预设 + (当前值不在预设里时的回显) + 自定义入口
function ctxOptions(current) {
  const n = Number(current) || 0;
  const opts = [...CTX_PRESETS];
  if (n > 0 && !CTX_PRESETS.some((p) => p.value === n)) {
    opts.push({ value: n, label: `${formatCtx(n)} (自定义)` });
  }
  opts.push({ value: CTX_CUSTOM, label: '自定义…' });
  return opts;
}
function formatCtx(n) {
  const v = Number(n) || 0;
  if (v >= 1000000) return (v / 1000000).toFixed(v % 1000000 === 0 ? 0 : 1) + 'M';
  if (v >= 1024) return Math.round(v / 1024) + 'K';
  return String(v);
}

// 后端 pricing JSON → [{ name, upName, rate, effort, ctx, enabled }]
// 每个 model name 都是一个独立行,管理员可以:开关、填上游映射名、改倍率、选思考强度、
// 设上下文窗口、或点"从上游拉取模型"一键填充
// upName = 上游真实模型名(渠道 model_map 映射),空 = 转发时原样用 name
// ctx = 上下文窗口 token 数,0 = 不限制
function pricingToRows(pricingStr, effortsStr, modelMapStr, ctxStr) {
  let pricing = {};
  try { pricing = pricingStr ? JSON.parse(pricingStr) : {}; } catch { pricing = {}; }
  let efforts = {};
  try { efforts = effortsStr ? JSON.parse(effortsStr) : {}; } catch { efforts = {}; }
  let modelMap = {};
  try { modelMap = modelMapStr ? JSON.parse(modelMapStr) : {}; } catch { modelMap = {}; }
  let ctxMap = {};
  try { ctxMap = ctxStr ? JSON.parse(ctxStr) : {}; } catch { ctxMap = {}; }
  // 注意:这里不再依赖"全局模型白名单",因为管理员可能正创建一个全新的渠道,
  // 要支持全新的模型名(比如 deepseek-chat)。
  // 渠道的 model_pricing 就是这个渠道支持哪些模型的唯一真值源。
  return Object.entries(pricing).map(([name, rate]) => {
    const ctx = Number(ctxMap[name]) > 0 ? Number(ctxMap[name]) : 0;
    return {
      name,
      upName: typeof modelMap[name] === 'string' ? modelMap[name] : '',
      rate: typeof rate === 'number' ? rate : 1,
      effort: EFFORT_LEVELS.includes(efforts[name]) ? efforts[name] : '',
      ctx,
      // 不在预设里的历史值 → 直接进"自定义"模式,避免下拉显示空白
      ctxCustom: ctx > 0 && !CTX_PRESETS.some((p) => p.value === ctx),
      enabled: true,
    };
  });
}

// 行数组 → reasoning_efforts JSON(只保留启用行且选了档位的)
function rowsToEfforts(rows) {
  const obj = {};
  rows.forEach((r) => {
    if (!r.enabled || !r.name || !r.name.trim()) return;
    if (r.effort) obj[r.name.trim()] = r.effort;
  });
  return JSON.stringify(obj);
}

// 行数组 → model_map JSON(只保留启用行且填了与对内名不同的上游名;
// 相同名字的映射没有意义,直接丢弃保持 JSON 干净)
function rowsToModelMap(rows) {
  const obj = {};
  rows.forEach((r) => {
    if (!r.enabled || !r.name || !r.name.trim()) return;
    const name = r.name.trim();
    const up = (r.upName || '').trim();
    if (up && up !== name) obj[name] = up;
  });
  return JSON.stringify(obj);
}

function rowsToPricing(rows) {
  const obj = {};
  rows.forEach((r) => {
    if (!r.enabled || !r.name || !r.name.trim()) return;
    obj[r.name.trim()] = Number(r.rate) || 1;
  });
  return JSON.stringify(obj);
}

// 行数组 → context_limits JSON(只保留启用行且窗口 > 0 的;0 = 不限制,直接不落键)
function rowsToContextLimits(rows) {
  const obj = {};
  rows.forEach((r) => {
    if (!r.enabled || !r.name || !r.name.trim()) return;
    const n = Math.floor(Number(r.ctx) || 0);
    if (n > 0) obj[r.name.trim()] = n;
  });
  return JSON.stringify(obj);
}

const AdminChannels = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyChannel });
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [fetching, setFetching] = useState(false);

  // 窄屏(手机)用卡片视图替代表格:表格 9 列 + fixed 操作列在手机上会互相遮挡
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [mobilePage, setMobilePage] = useState(1);

  // 桌面端表格分页:自己持有 currentPage,避免列表刷新时被 Semi 重置回第 1 页
  const TABLE_PAGE_SIZE = 20;
  const { page: tablePage, pagedList, setPage: setTablePage } = usePagedRows(list, TABLE_PAGE_SIZE);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 当前渠道的 model_pricing 编辑行
  // 每行 = { name, rate, enabled };enabled=false 的行会被剔除
  const [modelRows, setModelRows] = useState([]);

  const load = () => {
    setLoading(true);
    API.get('/api/admin/channels').then((r) => setList(r.data.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  // 渠道并发占用(在飞 / 排队),5 秒刷新一次。
  // 数据来自 /api/admin/stats/concurrency:渠道槽是上游保护,占用满了就是上游被打满,
  // 和「排队」要分开看 —— 排队说明并发上限偏低,在飞打满说明上游扛不住。
  const [usage, setUsage] = useState({});
  useEffect(() => {
    const poll = () => {
      API.get('/api/admin/stats/concurrency')
        .then((r) => {
          const map = {};
          (r.data.data?.channels || []).forEach((c) => { map[c.id] = c; });
          setUsage(map);
        })
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, []);

  // 单个渠道的占用展示:在飞/上限 + 排队数
  const usageCell = (row) => {
    const u = usage[row.id];
    if (!u) return <span style={{ color: '#c9cdd4' }}>—</span>;
    const max = u.max || 0;
    const full = max > 0 && u.inflight >= max;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
        <span style={{ color: full ? '#f56565' : '#1f2329', fontWeight: full ? 600 : 400 }}>
          {u.inflight}{max > 0 ? ' / ' + max : ''}
        </span>
        {u.queue > 0 && (
          <span style={{ color: '#ff7d00', fontSize: 11 }} title="排队等待空位的请求数">排 {u.queue}</span>
        )}
      </span>
    );
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyChannel });
    setModelRows([]); // 新渠道空白,等管理员填完 Base URL/Key 后点"从上游拉取"
    setModalOpen(true);
  };
  const openEdit = (row) => {
    API.get(`/api/admin/channels/${row.id}`)
      .then((r) => {
        const data = r.data.data;
        setEditing(data);
        setForm({ ...data });
        setModelRows(pricingToRows(data.model_pricing, data.reasoning_efforts, data.model_map, data.context_limits));
        setModalOpen(true);
      });
  };

  const validate = (v) => {
    if (!v.name?.trim()) return '请填写名称';
    if (!v.type) return '请选择类型';
    if (!v.base_url?.trim()) return '请填写 Base URL';
    if (!v.api_key?.trim()) return '请填写 API Key';
    return null;
  };

  const save = async () => {
    const v = {
      ...form,
      enabled: form.enabled ? 1 : 0,
      priority: Math.max(0, Math.min(9999, parseInt(form.priority, 10) || 0)),
      max_concurrency: Math.max(0, Math.min(200, parseInt(form.max_concurrency, 10) || 5)),
      model_pricing: rowsToPricing(modelRows),
      reasoning_efforts: rowsToEfforts(modelRows),
      model_map: rowsToModelMap(modelRows),
      context_limits: rowsToContextLimits(modelRows),
    };
    const err = validate(v);
    if (err) { Toast.error(err); return; }
    const enabledModels = modelRows.filter((r) => r.enabled && r.name?.trim());
    if (enabledModels.length === 0) {
      Toast.error('至少启用一个模型才能保存');
      return;
    }
    setSaving(true);
    try {
      if (editing && editing.id) {
        await API.put(`/api/admin/channels/${editing.id}`, v);
        Toast.success('已保存');
      } else {
        await API.post('/api/admin/channels', v);
        Toast.success('已添加');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      Toast.error(e.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const remove = (row) => {
    Modal.confirm({
      title: '删除渠道?',
      content: `将删除 ${row.name},此操作不可恢复`,
      onOk: async () => { await API.delete(`/api/admin/channels/${row.id}`); Toast.success('已删除'); load(); },
    });
  };

  const test = async (row) => {
    setTestingId(row.id);
    try {
      const r = await API.post(`/api/admin/channels/${row.id}/test`);
      if (r.data.success) Toast.success(`${row.name} 连通正常 (HTTP ${r.data.data?.http_status})`);
      else Toast.error(`${row.name}: ${r.data.message}`);
    } finally { setTestingId(null); }
  };

  // 从上游拉取可用模型 —— 合并进当前 modelRows(已存在的保留倍率,新增的默认倍率 1)
  const fetchUpstreamModels = async () => {
    if (!form.base_url?.trim() || !form.api_key?.trim()) {
      Toast.error('先填 Base URL 和 API Key 再拉取');
      return;
    }
    setFetching(true);
    try {
      const r = await API.post('/api/admin/channels/fetch-models', { base_url: form.base_url, api_key: form.api_key });
      const upstreamList = (r.data.data?.models || []).map((s) => s.trim()).filter(Boolean);
      if (upstreamList.length === 0) {
        Toast.warning('上游没返回任何可用模型');
        return;
      }
      setModelRows((prev) => {
        const seen = new Set(prev.map((r) => r.name));
        const merged = [...prev];
        upstreamList.forEach((name) => {
          if (!seen.has(name)) {
            merged.push({ name, upName: '', rate: 1, effort: '', ctx: CTX_DEFAULT, ctxCustom: false, enabled: true });
            seen.add(name);
          }
        });
        return merged;
      });
      Toast.success(`已拉取 ${upstreamList.length} 个模型`);
    } catch (e) {
      Toast.error(e.response?.data?.message || '拉取失败');
    } finally {
      setFetching(false);
    }
  };

  const updateModelRow = (idx, patch) => {
    setModelRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeModelRow = (idx) => {
    setModelRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const addManualRow = () => {
    setModelRows((prev) => [...prev, { name: '', upName: '', rate: 1, effort: '', ctx: CTX_DEFAULT, ctxCustom: false, enabled: true }]);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '名称', dataIndex: 'name', width: 130 },
    // 并发占用:在飞/上限 + 排队数(实时,5 秒刷新)。
    // 放在前面是为了不用横向滚动就能看到 —— 表格列多,放末尾会被滚动条藏掉。
    { title: '并发占用', dataIndex: 'id', width: 110, render: (_, row) => usageCell(row) },
    { title: '类型', dataIndex: 'type', width: 120, render: (t) => TYPES.find((x) => x.value === t)?.label || t },
    { title: 'Base URL', dataIndex: 'base_url', width: 240, ellipsis: true },
    {
      title: '支持模型', dataIndex: 'models', width: 260,
      render: (m) => {
        if (!m) return '—';
        // "MiniMax-M2,MiniMax-M2.1,MiniMax-M2.5,..." → 数组,方便 2 行省略
        const list = String(m).split(',').map((s) => s.trim()).filter(Boolean);
        const preview = list.slice(0, 2).join(', ') + (list.length > 2 ? ', ...' : '');
        return (
          <span
            onClick={(e) => {
              e.stopPropagation();
              Modal.info({
                title: '渠道支持的模型(共 ' + list.length + ' 个)',
                content: (
                  <div style={{ paddingTop: 8, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, lineHeight: 1.8, color: '#1f2329' }}>
                    {list.map((x) => (
                      <div key={x} style={{ padding: '2px 0', borderBottom: '1px dashed #f0f1f3' }}>{x}</div>
                    ))}
                  </div>
                ),
                okText: '关闭',
              });
            }}
            style={{
              cursor: 'pointer', color: '#1d7dfa',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-all',
              lineHeight: 1.4, maxHeight: '2.8em',
            }}
            title={'共 ' + list.length + ' 个模型,点击查看全部'}
          >{preview}</span>
        );
      },
    },
    { title: '优先级', dataIndex: 'priority', width: 80, render: (v) => (v ?? 0) },
    { title: '24h 调用', dataIndex: 'recent_calls', width: 90 },
    { title: '启用', dataIndex: 'enabled', width: 70, render: (e) => e ? '✓' : '—' },
    {
      title: '操作', width: 150, fixed: 'right',
      render: (_, row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          <Button size="small" theme="borderless" type="primary" style={{ padding: '0 6px' }} onClick={() => test(row)} loading={testingId === row.id}>测试</Button>
          <span style={{ color: '#c9cdd4', userSelect: 'none' }}>|</span>
          <Button size="small" theme="borderless" type="primary" style={{ padding: '0 6px' }} onClick={() => openEdit(row)}>编辑</Button>
          <span style={{ color: '#c9cdd4', userSelect: 'none' }}>|</span>
          <Button size="small" theme="borderless" type="danger" style={{ padding: '0 6px' }} onClick={() => remove(row)}>删除</Button>
        </span>
      ),
    },
  ];

  const rowStyle = { display: 'flex', alignItems: 'flex-start', marginBottom: 12 };
  // form-row:窄屏下由 CSS 把 label + 输入框改为上下堆叠
  const rowClass = 'form-row';
  const labelCell = { width: 90, fontSize: 13, color: '#4e5969', paddingTop: 6, flexShrink: 0 };
  const inputCell = { flex: 1 };

  // 手机端卡片视图:每条渠道一张卡,操作按钮独占一行,不再被 fixed 列遮挡
  const renderMobileCards = () => {
    const PAGE_SIZE = 8;
    const totalPage = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
    const page = Math.min(mobilePage, totalPage);
    const pageRows = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const cell = (label, value, color) => (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
        <span style={{ color: '#86909c', flexShrink: 0 }}>{label}</span>
        <span style={{ color: color || '#1f2329', textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
      </div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {pageRows.map((row) => {
          const models = row.models ? String(row.models).split(',').map((s) => s.trim()).filter(Boolean) : [];
          return (
            <div key={row.id} style={{ border: '1px solid #e5e6eb', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8, background: '#fff' }}>
              {/* 第一行:ID + 名称 + 启用状态 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#86909c' }}>#{row.id}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#1f2329', flex: 1, wordBreak: 'break-all' }}>{row.name}</span>
                <span style={{ fontSize: 12, color: row.enabled ? '#00b42a' : '#c9cdd4' }}>{row.enabled ? '✓ 启用' : '— 禁用'}</span>
              </div>
              {/* 信息区:两列网格 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                {cell('类型', TYPES.find((x) => x.value === row.type)?.label || row.type)}
                {cell('优先级', row.priority ?? 0)}
                {cell('24h 调用', row.recent_calls ?? 0)}
                {cell('模型数', models.length)}
                {cell('并发占用', usageCell(row))}
              </div>
              {/* Base URL 独占一行 */}
              {cell('Base URL', row.base_url || '—', '#4e5969')}
              {/* 支持模型 */}
              <div style={{ fontSize: 12 }}>
                <div style={{ color: '#86909c', marginBottom: 4 }}>支持模型({models.length})</div>
                <div style={{ color: '#1d7dfa', wordBreak: 'break-all', lineHeight: 1.5 }}>
                  {models.length ? models.slice(0, 3).join(', ') + (models.length > 3 ? ' …' : '') : '—'}
                </div>
              </div>
              {/* 操作区:整行铺开 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderTop: '1px solid #f2f3f5', paddingTop: 8 }}>
                <Button size="small" theme="borderless" type="primary" style={{ padding: '0 6px' }} onClick={() => test(row)} loading={testingId === row.id}>测试</Button>
                <span style={{ color: '#c9cdd4', userSelect: 'none' }}>|</span>
                <Button size="small" theme="borderless" type="primary" style={{ padding: '0 6px' }} onClick={() => openEdit(row)}>编辑</Button>
                <span style={{ color: '#c9cdd4', userSelect: 'none' }}>|</span>
                <Button size="small" theme="borderless" type="danger" style={{ padding: '0 6px' }} onClick={() => remove(row)}>删除</Button>
              </div>
            </div>
          );
        })}
        {list.length > PAGE_SIZE && (
          <Pagination
            total={list.length}
            pageSize={PAGE_SIZE}
            currentPage={page}
            onPageChange={(p) => setMobilePage(p)}
            size="small"
            style={{ alignSelf: 'center' }}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>渠道管理</h2>
        <Button type="primary" theme="solid" onClick={openNew}>+ 添加渠道</Button>
      </div>

      <div style={{ background: '#fff', borderRadius: 10, padding: 8, border: '1px solid #e5e6eb' }}>
        <Spin spinning={loading}>
          {isMobile ? (
            // 手机端:卡片视图。表格 9 列 + fixed 操作列在窄屏会互相遮挡
            renderMobileCards()
          ) : (
            <Table size="small" rowKey="id" columns={columns} dataSource={pagedList} scroll={{ x: 1410 }}
              pagination={{
                currentPage: tablePage,
                pageSize: TABLE_PAGE_SIZE,
                total: list.length,
                hideOnSinglePage: true,
                onPageChange: setTablePage,
              }} />
          )}
        </Spin>
      </div>

      <Modal
        title={editing && editing.id ? '编辑渠道' : '添加渠道'}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={960}
        style={{ maxWidth: '95vw' }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
            <Button onClick={() => setModalOpen(false)}>取消</Button>
            <Button type="primary" theme="solid" onClick={save} loading={saving}>保存</Button>
          </div>
        }
      >
        <div style={{ padding: '8px 0' }}>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>名称 <span style={{ color: '#f56565' }}>*</span></label>
            <div style={inputCell}>
              <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="例如 deepseek-main" />
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>类型 <span style={{ color: '#f56565' }}>*</span></label>
            <div style={inputCell}>
              <Select style={{ width: '100%' }} optionList={CHANNEL_OPTIONS} value={form.type} onChange={(v) => setForm({ ...form, type: v })} />
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>Base URL <span style={{ color: '#f56565' }}>*</span></label>
            <div style={inputCell}>
              <Input value={form.base_url} onChange={(v) => setForm({ ...form, base_url: v })} placeholder="https://api.deepseek.com/v1" />
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>API Key <span style={{ color: '#f56565' }}>*</span></label>
            <div style={inputCell}>
              <Input type="password" value={form.api_key} onChange={(v) => setForm({ ...form, api_key: v })} placeholder="上游服务商的 Key" />
            </div>
          </div>

          {/* 模型倍率区:每行可改模型名 + 倍率 + 开关,支持从上游一键拉取 */}
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>模型 & 倍率</label>
            <div style={inputCell}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <Button
                  size="small"
                  theme="outline"
                  loading={fetching}
                  disabled={!form.base_url?.trim() || !form.api_key?.trim()}
                  onClick={fetchUpstreamModels}
                >
                  从上游拉取
                </Button>
                <Button size="small" theme="borderless" type="primary" onClick={addManualRow}>+ 手动添加</Button>
                {modelRows.length > 0 && (
                  <Button size="small" theme="borderless" type="danger" onClick={() => setModelRows([])}>清空</Button>
                )}
              </div>
              {modelRows.length === 0 ? (
                <div style={{ border: '1px dashed #d9d9d9', borderRadius: 6, padding: 16, textAlign: 'center', color: '#86909c', fontSize: 12 }}>
                  填好 Base URL 和 API Key 后点"从上游拉取",或手动添加模型
                </div>
              ) : (
                <div style={{ border: '1px solid #e5e6eb', borderRadius: 6, padding: '8px 12px', background: '#fafbfc' }}>
                  <div className="model-grid-header" style={{ display: 'grid', gridTemplateColumns: '28px 1fr 1fr 80px 96px 104px 36px', gap: 8, alignItems: 'center', fontSize: 12, color: '#86909c', marginBottom: 6 }}>
                    <div></div>
                    <div title="用户请求时填的名字(白名单/倍率/思考强度/日志都按它)">模型名</div>
                    <div title="留空 = 同名直发;填了则在转发前改写(例如模型名 MiniMax-M3、上游名 MiniMax/MiniMax-M3)">上游模型名</div>
                    <div style={{ textAlign: 'center' }} title="每次成功调用按此倍率扣费">倍率</div>
                    <div style={{ textAlign: 'center' }} title="选「不指定」= 不干预请求;选了档位则强制注入 reasoning_effort,覆盖客户端传值">思考强度</div>
                    <div style={{ textAlign: 'center' }} title="网关内部的计费门槛:输入 token 超过它的部分按溢价倍率计费。不会下发给客户端,也不限制客户能用多大">上下文窗口</div>
                    <div></div>
                  </div>
                  {modelRows.map((row, idx) => (
                    <div key={idx} className="model-grid-row" style={{
                      display: 'grid',
                      gridTemplateColumns: '28px 1fr 1fr 80px 96px 104px 36px',
                      gap: 8,
                      alignItems: 'center',
                      marginBottom: 4,
                      opacity: row.enabled ? 1 : 0.45,
                    }}>
                      <div data-label="启用"><Switch size="small" checked={row.enabled} onChange={(v) => updateModelRow(idx, { enabled: v })} /></div>
                      <div data-label="模型名">
                        <Input
                          size="small"
                          value={row.name}
                          placeholder="MiniMax-M3"
                          onChange={(v) => updateModelRow(idx, { name: v })}
                        />
                      </div>
                      <div data-label="上游模型名">
                        <Input
                          size="small"
                          value={row.upName || ''}
                          placeholder="留空=同名直发"
                          onChange={(v) => updateModelRow(idx, { upName: v })}
                        />
                      </div>
                      <div data-label="倍率">
                        <InputNumber
                          size="small"
                          min={0}
                          step={0.5}
                          disabled={!row.enabled}
                          value={row.rate}
                          onChange={(v) => updateModelRow(idx, { rate: v })}
                        />
                      </div>
                      <div data-label="思考强度">
                        <Select
                          size="small"
                          optionList={EFFORT_OPTIONS}
                          disabled={!row.enabled}
                          value={row.effort || ''}
                          onChange={(v) => updateModelRow(idx, { effort: v })}
                        />
                      </div>
                      <div data-label="上下文窗口">
                        <Select
                          size="small"
                          optionList={ctxOptions(row.ctx)}
                          disabled={!row.enabled}
                          value={row.ctxCustom ? CTX_CUSTOM : (Number(row.ctx) || 0)}
                          onChange={(v) => {
                            if (v === CTX_CUSTOM) {
                              updateModelRow(idx, { ctxCustom: true });
                            } else {
                              updateModelRow(idx, { ctx: Number(v) || 0, ctxCustom: false });
                            }
                          }}
                          showClear={false}
                        />
                        {row.ctxCustom && (
                          <InputNumber
                            size="small"
                            min={0}
                            step={1024}
                            style={{ width: '100%', marginTop: 4 }}
                            disabled={!row.enabled}
                            value={Number(row.ctx) || 0}
                            onChange={(v) => updateModelRow(idx, { ctx: Math.floor(Number(v) || 0) })}
                          />
                        )}
                      </div>
                      <div data-label=""><Button size="small" theme="borderless" type="danger" onClick={() => removeModelRow(idx)}>×</Button></div>
                    </div>
                  ))}
                </div>
              )}
              <div
                className="form-hint"
                title="各列含义见表头提示。上下文窗口是网关内部的计费门槛:输入 token 超过它的部分按溢价倍率计费(上游对超窗输入按更高价计费,MiniMax 把输入 token 按 ≤512k / >512k 分两档,超过 512k 单价翻倍)。网关不会拿它去限制客户能用多大 —— 客户想调多大自己改客户端配置,超门槛的代价由发起方承担。"
              >
                各列含义见表头;上下文窗口是计费门槛,超窗的请求按溢价计费
              </div>
            </div>
          </div>

          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>优先级</label>
            <div style={inputCell}>
              <InputNumber min={0} max={9999} value={Number(form.priority) || 0} onChange={(v) => setForm({ ...form, priority: v })} />
              <div
                className="form-hint"
                title="数值越小越优先。请求先用优先级最高的渠道;它额度耗尽(402)、限流(429)或异常(5xx)失败后会进入冷却,请求自动降级到下一个优先级。主渠道填 0,备渠道填 10。"
              >
                数值越小越优先,主渠道失败后自动降级到下一个
              </div>
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>最大并发</label>
            <div style={inputCell}>
              <InputNumber
                min={0} max={200}
                value={form.max_concurrency ?? 5}
                onChange={(v) => setForm({ ...form, max_concurrency: v })}
              />
              <div
                className="form-hint"
                title="同时最多几个请求打向此上游渠道(超出排队等空闲,CHANNEL_QUEUE_TIMEOUT_S 秒后返回 503)。免费账号建议 2~5,付费账号 10~20,自建不限可设 50+。"
              >
                同时最多几个请求打到本渠道,超出排队等空闲(CHANNEL_QUEUE_TIMEOUT_S 秒后 503)
                {editing?.id && usage[editing.id] && (
                  <span style={{ color: '#1d7dfa' }}>
                    {' '}· 当前在飞 {usage[editing.id].inflight}
                    {usage[editing.id].queue > 0 ? `,排队 ${usage[editing.id].queue}` : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>启用</label>
            <div style={inputCell}>
              <Switch checked={!!form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} />
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>备注</label>
            <div style={inputCell}>
              <Input value={form.remark} onChange={(v) => setForm({ ...form, remark: v })} />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminChannels;
